import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import type { Ref } from 'vue'
import { PricingAction } from '../constants'
import type {
  ApiError,
  CreateNewPartRequest,
  CreateSellingOptionRequest,
  PartPricingItem,
  PricingAnalysisResponse,
  PricingControlState,
  PricingDecision,
  PricingDecisionsRequest,
  PricingDecisionsResponse,
} from '../types'
import { asBuyingOptionId, asCurrencyCode, asPartId, asPartOem, asPartSku, asSellingOptionId, asTransactionItemId } from '../types'

export function useBillPartPricingApi(state: Ref<PricingControlState>) {
  const loadPricingAnalysis = async (transactionId: string): Promise<void> => {
    if (!transactionId) {
      throw new Error('Transaction ID is required')
    }

    state.value.isLoading = true
    state.value.error = null

    try {
      const response = await axiosApi.get<PricingAnalysisResponse>(`/transaction/${transactionId}/part-pricing-analysis`)

      const analysisData = response.data

      // Helper: decide sensible default action per item
      const computeDefaultAction = (src: PricingAnalysisResponse['items'][number]): PricingAction => {
        // New parts default to creating a new part
        if (src.is_new_part) {
          return PricingAction.CREATE_NEW_PART
        }

        // If there is no price change, keep current
        if (src.price_difference === 0) {
          return PricingAction.KEEP
        }

        // Otherwise, default to updating price
        return PricingAction.UPDATE
      }

      // Transform API response to internal format
      const items: PartPricingItem[] = analysisData.items.map((item) => ({
        id: item.id,
        transactionItemId: asTransactionItemId(item.transaction_item_id),
        partId: item.part_id ? asPartId(item.part_id) : null,
        name: item.name,
        sku: asPartSku(item.sku),
        oem: asPartOem(item.oem),
        currentPrice: item.current_price,
        proposedPrice: item.proposed_price,
        priceDifference: item.price_difference,
        priceDifferencePercent: item.price_difference_percent,
        currency: asCurrencyCode(item.currency),
        sellingOptionId: item.selling_option_id ? asSellingOptionId(item.selling_option_id) : null,
        supplierName: item.supplier_name,
        lastUpdated: item.last_updated,
        isNewPart: item.is_new_part || false,
        hasPriceDifference: item.has_price_difference !== false, // Default to true if not specified
        // Pre-select most sensible action
        action: computeDefaultAction(item),
        // For existing parts (not new parts), enable selling option creation
        canCreateSellingOptions: item.can_create_selling_options ?? (!item.is_new_part && item.part_id !== null),
        // Use available buying options from backend API response
        availableBuyingOptions:
          item.available_buying_options?.map((option) => ({
            id: asBuyingOptionId(option.id),
            name: option.name,
            price: option.price,
            currency: asCurrencyCode(option.currency),
            supplierName: option.supplier_name,
            isDefault: option.is_default,
          })) || undefined,
      }))

      state.value.items = items
      state.value.hasUnsavedChanges = false
    } catch (error) {
      console.error('Failed to load pricing analysis:', error)

      const apiError = error as ApiError
      let errorMessage = 'Failed to load pricing analysis'

      if (apiError.status === 404) {
        errorMessage = 'Transaction not found or has no pricing analysis available'
      } else if (apiError.status === 403) {
        errorMessage = 'You do not have permission to access this transaction'
      } else if (apiError.message) {
        errorMessage = apiError.message
      }

      state.value.error = errorMessage
      throw new Error(errorMessage)
    } finally {
      state.value.isLoading = false
    }
  }

  const applyPricingDecisions = async (transactionId: string, decisions: PricingDecision[]): Promise<PricingDecisionsResponse> => {
    if (!decisions || decisions.length === 0) {
      throw new Error('No pricing decisions provided')
    }

    state.value.isProcessing = true
    state.value.error = null

    try {
      const requestData: PricingDecisionsRequest = { decisions }

      const response = await axiosApi.post<PricingDecisionsResponse>(`/transaction/${transactionId}/part-pricing-decisions`, requestData)

      return response.data
    } catch (error) {
      console.error('Failed to apply pricing decisions:', error)

      const apiError = error as ApiError
      let errorMessage = 'Failed to apply pricing decisions'

      if (apiError.status === 422) {
        errorMessage = 'Invalid pricing decisions provided'
      } else if (apiError.status === 403) {
        errorMessage = 'You do not have permission to modify these parts'
      } else if (apiError.message) {
        errorMessage = apiError.message
      }

      state.value.error = errorMessage
      throw new Error(errorMessage)
    } finally {
      state.value.isProcessing = false
    }
  }

  const refreshPricingAnalysis = async (transactionId: string): Promise<void> => {
    // Force refresh by clearing current data first
    state.value.items = []
    state.value.hasUnsavedChanges = false

    await loadPricingAnalysis(transactionId)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const validateTransaction = async (_transactionId: string): Promise<boolean> => {
    try {
      // TODO: Implement backend endpoint for transaction validation
      // const response = await axiosApi.get(`/transaction/${transactionId}/validate-for-pricing`)
      // return response.data.valid === true
      console.warn('Transaction validation endpoint not implemented in backend')
      return true // Assume valid for now
    } catch (error) {
      console.error('Failed to validate transaction:', error)
      return false
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getPricingHistory = async (_partId: string): Promise<unknown[]> => {
    try {
      // TODO: Implement backend endpoint for pricing history
      // const response = await axiosApi.get(`/parts/${partId}/pricing-history`)
      // return response.data.data || response.data
      console.warn('Pricing history endpoint not implemented in backend')
      return [] // Return empty array for now
    } catch (error) {
      console.error('Failed to get pricing history:', error)
      return []
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const previewPricingImpact = async (_decisions: PricingDecision[]): Promise<unknown> => {
    try {
      // TODO: Implement backend endpoint for pricing impact preview
      // const response = await axiosApi.post(`/transaction/preview-pricing-impact`, { decisions })
      // return response.data
      console.warn('Pricing impact preview endpoint not implemented in backend')
      return { success: true, impact: 0 } // Return mock data for now
    } catch (error) {
      console.error('Failed to preview pricing impact:', error)
      throw error
    }
  }

  const createSellingOption = async (
    sellingOptionData: CreateSellingOptionRequest,
  ): Promise<{ success: boolean; selling_option_id?: string; message?: string }> => {
    try {
      const response = await axiosApi.post('/parts/selling-options', sellingOptionData)
      return response.data
    } catch (error) {
      console.error('Failed to create selling option:', error)
      const apiError = handleApiError(error)
      throw new Error(apiError.message)
    }
  }

  const createNewPart = async (partData: CreateNewPartRequest): Promise<{ success: boolean; part_id?: string; message?: string }> => {
    try {
      const response = await axiosApi.post('/parts', partData)
      return response.data
    } catch (error) {
      console.error('Failed to create new part:', error)
      const apiError = handleApiError(error)
      throw new Error(apiError.message)
    }
  }

  const getPartBuyingOptions = async (partId: string): Promise<unknown[]> => {
    try {
      const response = await axiosApi.get(`/parts/${partId}/buying-options`)
      return response.data.data || response.data
    } catch (error) {
      console.error('Failed to get part buying options:', error)
      return []
    }
  }

  // Error handling helper
  const handleApiError = (error: unknown): ApiError => {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } }; message?: string }
      return {
        status: axiosError.response?.status || 500,
        message: axiosError.response?.data?.message || axiosError.message || 'Unknown error',
        data: axiosError.response?.data,
      }
    }

    return {
      status: 500,
      message: error instanceof Error ? error.message : 'Unknown error',
      data: null,
    }
  }

  return {
    loadPricingAnalysis,
    applyPricingDecisions,
    refreshPricingAnalysis,
    validateTransaction,
    getPricingHistory,
    previewPricingImpact,
    createSellingOption,
    createNewPart,
    getPartBuyingOptions,
    handleApiError,
  }
}
